// src/admin/RecentOrders.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { createPortal } from "react-dom";
import ApiURl from "../../controllers/Api";

// Move ModalShell outside the component and memoize it
const ModalShell = React.memo(({ onClose, children }) => {
  const overlayRef = useRef(null);
  
  const handleOverlayClick = useCallback((e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }, [onClose]);

  return createPortal(
    <div
      ref={overlayRef}
      className="__modal-overlay"
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100dvh",
        background: "rgba(43,0,0,0.34)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 2147483646,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        paddingTop: "5rem",
        paddingLeft: "1rem",
        paddingRight: "1rem",
      }}
    >
      <div onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
});

function RecentOrders() {
  const navigate = useNavigate();

  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentOrderIndex, setCurrentOrderIndex] = useState(0);

  const [filterVisible, setFilterVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [review, setReview] = useState({ reviews: "" });
  const [isReviewPopupVisible, setIsReviewPopupVisible] = useState(false);

  const [isInvoicePopupVisible, setIsInvoicePopupVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);

  const [isMobile, setIsMobile] = useState(false);

  const pageRef = useRef(null);
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const invoiceInputRef = useRef(null);

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role !== "admin") navigate("/unauthorized");
  }, [navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = () => setIsMobile(mq.matches);
    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Focus input when invoice popup opens
  useEffect(() => {
    if (isInvoicePopupVisible && invoiceInputRef.current) {
      setTimeout(() => {
        invoiceInputRef.current.focus();
      }, 100);
    }
  }, [isInvoicePopupVisible]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${ApiURl}/getDashboardData.php`);
      const data = await response.json();

      if (data?.success) {
        const d = data?.data || {};
        setRecentOrders(d.all_orders || []);
        setError(null);
        setLoading(false);
      } else {
        setError(data?.message || "Failed to fetch orders.");
        setLoading(false);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("An error occurred while fetching data. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!recentOrders?.length) return;
    const t = setInterval(() => {
      setCurrentOrderIndex((prev) => (prev + 1) % recentOrders.length);
    }, 2500);
    return () => clearInterval(t);
  }, [recentOrders]);

  const filteredOrders = useMemo(() => {
    return (recentOrders || []).filter((order) => {
      let ok = true;

      if (statusFilter && order.status !== statusFilter) ok = false;

      if (dateFrom) {
        const od = new Date(order.order_date);
        const df = new Date(dateFrom);
        if (od < df) ok = false;
      }

      if (dateTo) {
        const od = new Date(order.order_date);
        const dt = new Date(dateTo);
        dt.setHours(23, 59, 59, 999);
        if (od > dt) ok = false;
      }

      return ok;
    });
  }, [recentOrders, statusFilter, dateFrom, dateTo]);

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ordersPerPage));

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (filteredOrders.length === 0) setCurrentPage(1);
  }, [filteredOrders.length, totalPages, currentPage]);

  const toggleFilterPanel = () => setFilterVisible((v) => !v);

  const resetFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!pageRef.current) return;

    gsap.fromTo(
      pageRef.current,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" }
    );

    if (tableRef.current) {
      gsap.fromTo(
        tableRef.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, ease: "power2.out", delay: 0.12 }
      );
    }
  }, [loading]);

  const statusPill = (status) => {
    if (status === "completed")
      return "bg-[#edf7ee] text-[#47704e] ring-1 ring-[#cfe2d1]";
    if (status === "pending")
      return "bg-[#fff7df] text-[#8a651b] ring-1 ring-[#ead9a7]";
    if (status === "cancelled")
      return "bg-[#fff0ee] text-[#9d3d36] ring-1 ring-[#edcbc7]";
    return "bg-[#f1e8e0] text-[#6d5b53] ring-1 ring-[#dfd1c6]";
  };

  const cardShell = "rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_20px_55px_rgba(43,0,0,0.08)] transition-all duration-300 hover:shadow-[0_26px_65px_rgba(43,0,0,0.11)]";

  const showInvoicePopup = (orderId) => {
    setSelectedOrderId(orderId);
    setInvoiceNumber("");
    setIsInvoicePopupVisible(true);
  };

  const handleInvoiceSubmit = async () => {
    if (!invoiceNumber.trim()) {
      alert("Please enter an invoice number.");
      return;
    }

    try {
      const response = await fetch(`${ApiURl}/updateOrderStatus.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: selectedOrderId,
          status: "completed",
          invoice_number: invoiceNumber,
        }),
      });

      const result = await response.json();

      if (result?.success) {
        setRecentOrders((prev) =>
          prev.map((o) =>
            o.order_id === selectedOrderId
              ? { ...o, status: "completed", invoice_number: invoiceNumber }
              : o
          )
        );
        setIsInvoicePopupVisible(false);
        setInvoiceNumber("");
        alert("Order updated successfully with invoice number!");
      } else {
        alert(result?.message || "Failed to update order status.");
      }
    } catch {
      alert("An error occurred while updating the order status.");
    }
  };

  const cancelOrderStatus = async (orderId, userName, userId) => {
    const isConfirmed = window.confirm(`Are you sure you want to cancel order #${orderId}?`);
    if (!isConfirmed) return;

    try {
      let userPhone = null;

      if (userId) {
        const userResponse = await fetch(`${ApiURl}/getUserDetails.php?user_id=${userId}`);
        const userResult = await userResponse.json();
        userPhone = userResult?.data?.phone || null;
      }

      const response = await fetch(`${ApiURl}/updateOrderStatus.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, status: "cancelled" }),
      });

      const result = await response.json();

      if (result?.success) {
        setRecentOrders((prev) =>
          prev.map((o) => (o.order_id === orderId ? { ...o, status: "cancelled" } : o))
        );

        if (!userPhone) {
          alert("Order cancelled, but phone not found for WhatsApp.");
          return;
        }

        const whatsappResponse = await fetch(`${ApiURl}/sendWhatsAppMessage.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_name: userName,
            phone: userPhone,
            order_id: orderId,
          }),
        });

        const whatsappResult = await whatsappResponse.json();
        if (whatsappResult?.status === "success") {
          alert("Order cancelled and WhatsApp message sent!");
        } else {
          alert(whatsappResult?.message || "Cancelled but WhatsApp failed.");
        }
      } else {
        alert(result?.message || "Failed to cancel order.");
      }
    } catch (error) {
      console.error(error);
      alert("Error cancelling order.");
    }
  };

  const showReviewPopup = async (orderId) => {
    try {
      const response = await fetch(`${ApiURl}/getReview.php?order_id=${orderId}`);
      const data = await response.json();

      if (data?.success) {
        setReview({
          reviews: data?.reviews?.length ? data.reviews : "No review available.",
        });
      } else {
        setReview({ reviews: "Error fetching review." });
      }

      setIsReviewPopupVisible(true);
    } catch {
      setReview({ reviews: "Error fetching review." });
      setIsReviewPopupVisible(true);
    }
  };

  // lock background scroll
  useEffect(() => {
    const anyOpen = filterVisible || isReviewPopupVisible || isInvoicePopupVisible;
    if (!anyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filterVisible, isReviewPopupVisible, isInvoicePopupVisible]);

  const scrollTableLeft = () => {
    if (tableContainerRef.current) tableContainerRef.current.scrollBy({ left: -320, behavior: "smooth" });
  };
  const scrollTableRight = () => {
    if (tableContainerRef.current) tableContainerRef.current.scrollBy({ left: 320, behavior: "smooth" });
  };

  const getDisplayedPages = () => {
    const delta = isMobile ? 1 : 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7EFE7] p-4 font-[Kanit]">
        <div className="w-full max-w-md rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-8 text-center shadow-[0_24px_70px_rgba(43,0,0,0.12)]">
          <div className="relative">
            <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-solid border-[#8f6f38] border-r-transparent"></div>
          </div>
          <p className="mt-4 font-medium text-[#6f5e57]">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7EFE7] p-4 font-[Kanit]">
        <div className="w-full max-w-md rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-8 text-center shadow-[0_24px_70px_rgba(43,0,0,0.12)]">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Error Loading Orders</h3>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchOrders}
            className="px-4 py-2 bg-[#b29063] text-white rounded-lg hover:bg-[#9e7e53] transition text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="recent-orders-luxury min-h-screen bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-4 rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.12),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">
            Recent Orders
          </h1>
          <p className="mt-2 text-sm text-[#6f5e57]">
            Showing {currentOrders.length} of {filteredOrders.length} orders
          </p>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-2 sm:mt-0">
          <button
            onClick={fetchOrders}
            className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:bg-[#fff7ed]"
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </span>
          </button>
          
          <button
            onClick={toggleFilterPanel}
            className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B]"
          >
            Filter Orders
          </button>

          <button
            onClick={() => navigate("/admin")}
            className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:bg-[#fff7ed]"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {filterVisible && (
        <ModalShell onClose={() => setFilterVisible(false)}>
          <div className="w-full max-w-sm rounded-[24px] border border-[#d4af37]/25 bg-[#fffaf5] p-5 shadow-[0_30px_90px_rgba(43,0,0,0.22)]">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-serif text-xl font-medium text-[#2B0000]">Filters</h3>
              <button
                onClick={() => setFilterVisible(false)}
                className="text-xl text-[#9a8981] transition hover:text-[#2B0000]"
                aria-label="Close filters"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">Order Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">Date From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">Date To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={resetFilters}
                  className="flex-1 rounded-xl border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                >
                  Reset
                </button>
                <button
                  onClick={() => setFilterVisible(false)}
                  className="flex-1 rounded-xl bg-[#2B0000] px-3 py-2.5 text-sm font-semibold text-[#f4dfae] transition hover:bg-[#4A090B]"
                >
                  Apply
                </button>
              </div>

              <p className="text-[11px] text-[#7d6c64]">Filters update instantly.</p>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ORDERS TABLE */}
      <div className={`${cardShell} p-4 sm:p-6`} ref={tableRef}>
        <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
          <div>
            <h3 className="font-serif text-2xl font-medium text-[#2B0000]">Orders List</h3>
            <p className="text-xs text-[#7d6c64]">
              Showing {currentOrders.length} of {filteredOrders.length} orders
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <select
                value={ordersPerPage}
                onChange={(e) => {
                  setOrdersPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-xl border border-[#d6c4b5] bg-white px-3 py-2.5 text-xs font-semibold text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={scrollTableLeft}
                className="inline-flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-xs font-semibold text-[#2B0000] transition hover:bg-[#fff7ed] sm:text-sm"
                aria-label="Scroll table left"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={scrollTableRight}
                className="inline-flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-xs font-semibold text-[#2B0000] transition hover:bg-[#fff7ed] sm:text-sm"
                aria-label="Scroll table right"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          ref={tableContainerRef}
          className="overflow-x-auto rounded-[20px] border border-[#d8c6b7] bg-white shadow-[0_14px_36px_rgba(43,0,0,0.05)] scroll-smooth overscroll-x-contain"
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            scrollbarWidth: "thin",
            scrollbarColor: "#8f6f38 #f4e8dd",
          }}
        >
          <table className="w-full min-w-[980px] table-auto bg-white">
            <thead className="sticky top-0 z-10 bg-[#eee2d7]">
              <tr className="border-b border-[#d8c6b7]">
                <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-[#4b352f]">Order No</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-[#4b352f]">Date & Time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-[#4b352f]">Price</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-[#4b352f]">Status</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-[#4b352f]">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentOrders.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={6}>
                    No orders found for current filters.
                  </td>
                </tr>
              ) : (
                currentOrders.map((order) => (
                  <tr
                    key={order.order_id}
                    className="border-b border-[#eadfd5] transition hover:bg-[#fff5ea]"
                  >
                    <td className="px-4 py-4 text-sm text-[#2B0000]">#{order.order_id}</td>
                    <td className="px-4 py-4 text-sm text-[#6f5e57]">{order.order_date}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-[#2B0000]">₹{order.total_amount}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusPill(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/admin/AdminViewDetails/${order.order_id}`)}
                          className="whitespace-nowrap rounded-lg bg-[#8f6f38] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#765a2d]"
                        >
                          Details
                        </button>

                        {order.status !== "cancelled" ? (
                          <button
                            onClick={() => navigate(`/admin/AdminReceipt/${order.order_id}`)}
                            className="whitespace-nowrap rounded-lg bg-[#2B0000] px-3 py-2 text-xs font-semibold text-[#f4dfae] transition hover:bg-[#4A090B]"
                          >
                            Bill
                          </button>
                        ) : (
                          <button
                            onClick={() => showReviewPopup(order.order_id)}
                            className="whitespace-nowrap rounded-lg border border-rose-200 bg-[#fff1ef] px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-600 hover:text-white"
                          >
                            View
                          </button>
                        )}

                        <button
                          onClick={() => showInvoicePopup(order.order_id)}
                          disabled={order.status === "completed" || order.status === "cancelled"}
                          className={`rounded-lg px-3 py-2 text-xs font-semibold transition whitespace-nowrap ${
                            order.status === "completed" || order.status === "cancelled"
                              ? "cursor-not-allowed bg-[#eee5dd] text-[#9a8981]"
                              : "bg-[#6f7f5e] text-white hover:bg-[#5f704f]"
                          }`}
                        >
                          Complete
                        </button>

                        <button
                          onClick={() => cancelOrderStatus(order.order_id, order.full_name, order.user_id)}
                          disabled={order.status === "completed" || order.status === "cancelled"}
                          className={`rounded-lg px-3 py-2 text-xs font-semibold transition whitespace-nowrap ${
                            order.status === "completed" || order.status === "cancelled"
                              ? "cursor-not-allowed bg-[#eee5dd] text-[#9a8981]"
                              : "bg-[#a2463d] text-white hover:bg-[#8f3932]"
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredOrders.length > 0 && (
          <div className="mt-6 flex flex-col items-stretch justify-between gap-3 border-t border-[#e3d6cb] pt-5 sm:flex-row sm:items-center">
            <div className="text-sm text-[#6f5e57]">
              <span className="hidden sm:inline">
                Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of{" "}
                {filteredOrders.length} entries
              </span>
              <span className="sm:hidden">
                Page {currentPage} of {totalPages}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`rounded-lg px-3 sm:px-4 py-2 text-sm font-semibold transition ${
                  currentPage === 1 ? "cursor-not-allowed bg-[#eee5dd] text-[#a2928b]" : "border border-[#d6c4b5] bg-white text-[#2B0000] hover:bg-[#fff7ed]"
                }`}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>

              <div className="max-w-[60vw] sm:max-w-none overflow-x-auto overscroll-x-contain">
                <div className="flex items-center gap-1 px-1">
                  {getDisplayedPages().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span key={`dots-${index}`} className="px-2 py-2 text-gray-400 select-none">
                        …
                      </span>
                    ) : (
                      <button
                        key={`page-${pageNum}`}
                        onClick={() => paginate(pageNum)}
                        className={`rounded-lg px-3 py-2 sm:py-1 text-sm font-semibold transition whitespace-nowrap ${
                          currentPage === pageNum ? "bg-[#2B0000] text-[#f4dfae]" : "border border-[#d6c4b5] bg-white text-[#2B0000] hover:bg-[#fff7ed]"
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                </div>
              </div>

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`rounded-lg px-3 sm:px-4 py-2 text-sm font-semibold transition ${
                  currentPage === totalPages ? "cursor-not-allowed bg-[#eee5dd] text-[#a2928b]" : "border border-[#d6c4b5] bg-white text-[#2B0000] hover:bg-[#fff7ed]"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">Next</span>
              </button>
            </div>

            <div className="sm:hidden">
              <select
                value={ordersPerPage}
                onChange={(e) => {
                  setOrdersPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-xs font-semibold text-[#2B0000]"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* REVIEW POPUP */}
      {isReviewPopupVisible && (
        <ModalShell onClose={() => setIsReviewPopupVisible(false)}>
          <div className="w-full max-w-md rounded-[24px] border border-[#d4af37]/25 bg-[#fffaf5] p-6 shadow-[0_30px_90px_rgba(43,0,0,0.22)]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl font-medium text-[#2B0000]">Order Review</h3>
              <button
                onClick={() => setIsReviewPopupVisible(false)}
                className="text-xl text-[#9a8981] transition hover:text-[#2B0000]"
              >
                ×
              </button>
            </div>
            <div className="rounded-xl border border-[#decfc3] bg-[#fbf3eb] p-4">
              <p className="whitespace-pre-line text-sm leading-6 text-[#5d4b44]">{review.reviews}</p>
            </div>
            <div className="mt-6">
              <button
                onClick={() => setIsReviewPopupVisible(false)}
                className="w-full rounded-xl bg-[#2B0000] px-4 py-3 text-sm font-semibold text-[#f4dfae] transition hover:bg-[#4A090B]"
              >
                Close
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* INVOICE POPUP */}
      {isInvoicePopupVisible && (
        <ModalShell onClose={() => setIsInvoicePopupVisible(false)}>
          <div className="w-full max-w-md rounded-[24px] border border-[#d4af37]/25 bg-[#fffaf5] p-6 shadow-[0_30px_90px_rgba(43,0,0,0.22)]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl font-medium text-[#2B0000]">Complete Order #{selectedOrderId}</h3>
              <button
                onClick={() => setIsInvoicePopupVisible(false)}
                className="text-xl text-[#9a8981] transition hover:text-[#2B0000]"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-[#4a352f]">Invoice Number</label>
                <input
                  ref={invoiceInputRef}
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Enter invoice number"
                  className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsInvoicePopupVisible(false)}
                  className="flex-1 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvoiceSubmit}
                  className="flex-1 rounded-xl bg-[#6f7f5e] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#5f704f]"
                >
                  Mark as Complete
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      <style>{`
        .recent-orders-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .recent-orders-luxury button,
        .recent-orders-luxury input,
        .recent-orders-luxury select {
          font: inherit;
        }

        .recent-orders-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .recent-orders-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .recent-orders-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.55) rgba(212,175,55,.07);
        }

        .recent-orders-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .recent-orders-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .recent-orders-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.55);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .recent-orders-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .recent-orders-luxury *,
          .recent-orders-luxury *::before,
          .recent-orders-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default RecentOrders;